/**
 * A sheet that slides up from the bottom of the screen, flush with its edges (a sidebar from the
 * right on a wide screen): a grab bar, a title (a back button before it on a deeper page, see
 * SheetPages.tsx), a close button at the top right, scrolling content.
 * Dragged down (or right) from anywhere it goes away (dragDismiss.ts). It tells the game how much
 * of the screen it covers, so the world keeps the planet in the middle of the rest, and slides
 * back down when it closes (the App keeps it mounted for that moment, see SheetLeaving).
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type CSSProperties,
  type JSX,
  type ReactNode,
} from 'react';

import { setInset } from '@game/view/panels';
import { t } from '@i18n/index';
import { useGame } from '@ui/hooks';
import { Icon } from '@ui/icons';

import { useDragDismiss } from './dragDismiss';

import './sheet.css';

/** Wide and landscape (the web on a desktop): the sheet becomes a sidebar. */
export const SIDEBAR_QUERY = '(min-width: 760px) and (orientation: landscape)';
/** How long a sheet takes to slide away; the App unmounts it after this. */
export const SHEET_LEAVE_MS = 300;

/** True while the sheet is on its way out. */
export const SheetLeaving = createContext(false);

const isSidebar = (): boolean => matchMedia(SIDEBAR_QUERY).matches;

export function Sheet({
  id,
  title,
  onClose,
  children,
  className,
  free = false,
  badge,
  tone,
  back,
}: {
  id: string;
  title: string;
  /** On a deeper page: back to the one before. */
  back?: (() => void) | undefined;
  /** Beside the title: a level, a state. */
  badge?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  /** The planet beside it stays free to turn and walk on, and a tap there doesn't close it. */
  free?: boolean;
  /** A colour of its own (a machine's): `--tone` for everything in it. */
  tone?: string | undefined;
}): JSX.Element {
  const game = useGame();
  const leaving = useContext(SheetLeaving);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = panel.current;
    if (!el) return;
    if (leaving) {
      setInset(game, { bottom: 0, right: 0 });
      return;
    }
    const report = (): void => {
      const box = el.getBoundingClientRect();
      setInset(
        game,
        isSidebar() ? { bottom: 0, right: box.width } : { bottom: box.height, right: 0 },
      );
    };
    const observer = new ResizeObserver(report);
    observer.observe(el);
    report();
    el.focus({ preventScroll: true });
    return () => {
      observer.disconnect();
      setInset(game, { bottom: 0, right: 0 });
    };
  }, [game, leaving]);
  useDragDismiss(panel, isSidebar, onClose, !leaving);
  return (
    <div
      className={`sheet-wrap${leaving ? ' leaving' : ''}${free ? ' free' : ''}`}
      onPointerDown={(e) => {
        if (!free && e.target === e.currentTarget && !leaving) onClose();
      }}
    >
      <div
        className={`sheet panel${className ? ` ${className}` : ''}`}
        style={tone ? ({ '--tone': tone } as CSSProperties) : undefined}
        role="dialog"
        aria-labelledby={`${id}-title`}
        aria-hidden={leaving}
        ref={panel}
        tabIndex={-1}
      >
        <i className="grab" aria-hidden="true" />
        <div className="sheet-head">
          {back && (
            <button type="button" className="back" aria-label={t('ui.back')} onClick={back}>
              <Icon id="back" />
            </button>
          )}
          {/* a new title fades in: the page changed */}
          <div className="sheet-title" key={title}>
            <h2 id={`${id}-title`}>{title}</h2>
            {badge}
          </div>
          <button type="button" className="x" aria-label={t('ui.close')} onClick={onClose}>
            <Icon id="x" />
          </button>
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </div>
  );
}

export function Face({
  color,
  className = 'face',
}: {
  color: string;
  className?: string;
}): JSX.Element {
  return <span className={className} style={{ background: color }} />;
}
